import React, { useState, useEffect, useCallback } from 'react'
import styles from '../AdminDashboard.module.scss'
import ConfirmDialog from './ConfirmDialog'
import { config } from '../../../config'

interface ESIndex {
  name: string
  health: string
  status: string
  primary_shards: number
  replica_shards: number
  docs_count: number
  docs_deleted: number
  store_size: string
  primary_store_size: string
  indexing_total: number
  search_total: number
}

interface ESHealth {
  cluster_name: string
  status: string
  number_of_nodes: number
  number_of_data_nodes: number
  active_primary_shards: number
  active_shards: number
  unassigned_shards: number
  active_shards_percent: number
}

interface ESStats {
  indices_count: number
  docs_count: number
  store_size_bytes: number
}

interface IndicesResponse {
  success: boolean
  indices: ESIndex[]
  totals: {
    total_indices: number
    total_docs: number
    healthy: number
    yellow: number
    red: number
  }
}

interface HealthResponse {
  success: boolean
  health: ESHealth
  stats: ESStats
}

const ElasticsearchManager: React.FC = () => {
  const [indices, setIndices] = useState<ESIndex[]>([])
  const [health, setHealth] = useState<ESHealth | null>(null)
  const [stats, setStats] = useState<ESStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterPattern, setFilterPattern] = useState('feed-*')
  const [selectedIndices, setSelectedIndices] = useState<Set<string>>(new Set())
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    indexName: string | null
    isBulk: boolean
  }>({
    isOpen: false,
    title: '',
    message: '',
    indexName: null,
    isBulk: false,
  })
  const [actionLoading, setActionLoading] = useState(false)

  const fetchHealth = useCallback(async () => {
    try {
      const response = await fetch(`${config.API_URL}/admin/elasticsearch/health`)
      if (!response.ok) throw new Error('Failed to fetch health')
      const data: HealthResponse = await response.json()
      setHealth(data.health)
      setStats(data.stats)
    } catch (err) {
      console.error('Health fetch error:', err)
    }
  }, [])

  const fetchIndices = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(
        `${config.API_URL}/admin/elasticsearch/indices?pattern=${encodeURIComponent(filterPattern)}`,
      )
      if (!response.ok) throw new Error('Failed to fetch indices')
      const data: IndicesResponse = await response.json()
      setIndices(data.indices)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [filterPattern])

  useEffect(() => {
    fetchHealth()
    fetchIndices()
  }, [fetchHealth, fetchIndices])

  const handleDeleteClick = (indexName: string, docsCount: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Index',
      message: `Are you sure you want to delete "${indexName}"? This will permanently remove ${docsCount.toLocaleString()} document(s).`,
      indexName,
      isBulk: false,
    })
  }

  const handleBulkDeleteClick = () => {
    const totalDocs = indices.filter((i) => selectedIndices.has(i.name)).reduce((sum, i) => sum + i.docs_count, 0)

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Multiple Indices',
      message: `Are you sure you want to delete ${
        selectedIndices.size
      } indices? This will permanently remove ${totalDocs.toLocaleString()} document(s). This action cannot be undone!`,
      indexName: null,
      isBulk: true,
    })
  }

  const handleConfirm = async () => {
    setActionLoading(true)
    try {
      if (confirmDialog.isBulk) {
        const response = await fetch(`${config.API_URL}/admin/elasticsearch/indices-bulk`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ indices: Array.from(selectedIndices) }),
        })
        if (!response.ok) throw new Error('Bulk delete failed')
        setSelectedIndices(new Set())
      } else {
        const response = await fetch(
          `${config.API_URL}/admin/elasticsearch/indices/${encodeURIComponent(confirmDialog.indexName!)}`,
          { method: 'DELETE' },
        )
        if (!response.ok) throw new Error('Delete failed')
      }

      await fetchIndices()
      await fetchHealth()
      setConfirmDialog({ ...confirmDialog, isOpen: false })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setActionLoading(false)
    }
  }

  const handleFlush = async (indexName: string) => {
    try {
      const response = await fetch(
        `${config.API_URL}/admin/elasticsearch/indices/${encodeURIComponent(indexName)}/flush`,
        { method: 'POST' },
      )
      if (!response.ok) throw new Error('Flush failed')
      await fetchIndices()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Flush failed')
    }
  }

  const handleRefresh = async (indexName: string) => {
    try {
      const response = await fetch(
        `${config.API_URL}/admin/elasticsearch/indices/${encodeURIComponent(indexName)}/refresh`,
        { method: 'POST' },
      )
      if (!response.ok) throw new Error('Refresh failed')
      await fetchIndices()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refresh failed')
    }
  }

  const toggleSelectAll = () => {
    if (selectedIndices.size === indices.length) {
      setSelectedIndices(new Set())
    } else {
      setSelectedIndices(new Set(indices.map((i) => i.name)))
    }
  }

  const toggleSelect = (indexName: string) => {
    const newSet = new Set(selectedIndices)
    if (newSet.has(indexName)) {
      newSet.delete(indexName)
    } else {
      newSet.add(indexName)
    }
    setSelectedIndices(newSet)
  }

  const getHealthColor = (health: string): string => {
    switch (health) {
      case 'green':
        return styles.healthGreen
      case 'yellow':
        return styles.healthYellow
      case 'red':
        return styles.healthRed
      default:
        return ''
    }
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  if (loading && indices.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <span>Loading Elasticsearch data...</span>
      </div>
    )
  }

  return (
    <div className={styles.managerContainer}>
      {/* Health Banner */}
      {health && (
        <div className={`${styles.healthBanner} ${getHealthColor(health.status)}`}>
          <div className={styles.healthInfo}>
            <span className={styles.clusterName}>{health.cluster_name}</span>
            <span className={`${styles.healthStatus} ${getHealthColor(health.status)}`}>
              {health.status.toUpperCase()}
            </span>
          </div>
          <div className={styles.healthStats}>
            <span>Nodes: {health.number_of_nodes}</span>
            <span>Shards: {health.active_shards}</span>
            {stats && (
              <>
                <span>Indices: {stats.indices_count}</span>
                <span>Docs: {stats.docs_count.toLocaleString()}</span>
                <span>Size: {formatBytes(stats.store_size_bytes)}</span>
              </>
            )}
          </div>
        </div>
      )}

      <div className={styles.managerHeader}>
        <div className={styles.headerLeft}>
          <h2>Index Management</h2>
          <span className={styles.badge}>{indices.length} indices</span>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.filterInput}>
            <input
              type="text"
              value={filterPattern}
              onChange={(e) => setFilterPattern(e.target.value)}
              placeholder="Filter pattern (e.g., feed-*)"
            />
            <button onClick={fetchIndices}>Apply</button>
          </div>
          <button className={styles.refreshButton} onClick={fetchIndices}>
            ↻ Refresh
          </button>
          {selectedIndices.size > 0 && (
            <button className={styles.dangerButton} onClick={handleBulkDeleteClick}>
              Delete Selected ({selectedIndices.size})
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedIndices.size === indices.length && indices.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Index Name</th>
              <th>Health</th>
              <th>Docs</th>
              <th>Size</th>
              <th>Shards</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {indices.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyRow}>
                  No indices found matching pattern &quot;{filterPattern}&quot;
                </td>
              </tr>
            ) : (
              indices.map((index) => (
                <tr key={index.name} className={selectedIndices.has(index.name) ? styles.selectedRow : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIndices.has(index.name)}
                      onChange={() => toggleSelect(index.name)}
                    />
                  </td>
                  <td className={styles.indexName}>
                    <code>{index.name}</code>
                  </td>
                  <td>
                    <span className={`${styles.healthDot} ${getHealthColor(index.health)}`}>●</span>
                    {index.health}
                  </td>
                  <td>{index.docs_count.toLocaleString()}</td>
                  <td>{index.store_size}</td>
                  <td>
                    {index.primary_shards}p / {index.replica_shards}r
                  </td>
                  <td className={styles.actionButtons}>
                    <button
                      className={styles.actionButton}
                      onClick={() => handleRefresh(index.name)}
                      title="Refresh index">
                      ⟳
                    </button>
                    <button className={styles.actionButton} onClick={() => handleFlush(index.name)} title="Flush index">
                      ⏏
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteClick(index.name, index.docs_count)}
                      title="Delete index">
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        isDanger={true}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        isLoading={actionLoading}
      />
    </div>
  )
}

export default ElasticsearchManager
